import { createSlice } from '@reduxjs/toolkit';
import { AppThunk } from '.';
import { setLoading, setError } from './Site';
import { Activity } from '../models/plmt/Activity';
import { Country } from '../models/Country';
import { SelectOption } from '../models/Common';
import { format } from 'date-fns';
import { Notification } from '../models/plmt/Notification';
import { ComplianceSummary } from '../models/plmt/ComplianceSummary';
import { SigningEntityInfo } from '../models/plmt/SigningEntityInfo';
import { AnalysisResponse } from '../models/plmt/AnalysisResponse';
import { PartyInfo } from '../models/plmt/PartyInfo';
import { ComplianceReport } from '../models/plmt/ComplianceReport';
import { PrpData } from '../models/prp/PrpData';
import _ from 'lodash';

export interface PlmtState {
    activities?: Activity[];
    countries?: Country[];
    signingEntities?: SigningEntityInfo[];
    parties?: PartyInfo[];
    notification?: Notification;
    complianceSummaries?: ComplianceSummary[];
    complianceAnalysis?: AnalysisResponse;
    partnerTypes?: SelectOption[];
    distributors?: SelectOption[];
    complianceReports?: ComplianceReport[];
    complianceReport?: ComplianceReport;
    compliancePartners?: SelectOption[];
    compliancePrograms?: SelectOption[];
    geographies?: string[];
    prpData?: PrpData;
}

let initialState: PlmtState = {
}

export const plmtSlice = createSlice({
    name: 'Plmt',
    initialState,
    reducers: {
        setActivities: (state, action) => {
            state.activities = action.payload;
        },
        setCountries: (state, action) => {
            state.countries = action.payload;
        },
        setSigningEntities: (state, action) => {
            state.signingEntities = action.payload;
        },
        setNotification: (state, action) => {
            state.notification = action.payload;
        },
        setPartnerTypes: (state, action) => {
            state.partnerTypes = action.payload;
        },
        setComplianceSummaries: (state, action) => {
            state.complianceSummaries = action.payload;
        },
        setGapAnalysis: (state, action) => {
            state.complianceAnalysis = action.payload;
        },
        setParties: (state, action) => {
            state.parties = action.payload;
        },
        setDistributors: (state, action) => {
            state.distributors = action.payload;
        },
        setComplianceReport: (state, action) => {
            state.complianceReport = action.payload;
        },
        setComplianceReports: (state, action) => {
            state.complianceReports = action.payload;
        },
        setCompliancePrograms: (state, action) => {
            state.compliancePrograms = action.payload;
        },
        setCompliancePartners: (state, action) => {
            state.compliancePartners = action.payload;
        },
        setGeographies: (state, action) => {
            state.geographies = action.payload;
        },
        setPrpData: (state, action) => {
            state.prpData = action.payload;
        }
    }
});

export const { setActivities, setCountries, setSigningEntities, setNotification, setPartnerTypes, setComplianceSummaries, setComplianceReport,
    setGapAnalysis, setParties, setDistributors, setComplianceReports, setCompliancePrograms, setCompliancePartners,
    setGeographies, setPrpData } = plmtSlice.actions;

export const retrieveLearnerIds = (info: any): AppThunk => async (dispatch) => {
    let url = 'api/plmt/LookupLearnerIds';
    let data = new FormData();
    let fileName = ';'

    if (info.emails) {
        data.append("idList", info.emails);
    }
    if (info.emailFile && info.emailFile.length !== 0) {
        let idFile = info.emailFile[0];

        data.append("importFile", idFile, idFile.name);
    }
    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: data
    }).then(res => {
        if (res.ok) {
            fileName = res.headers.get('Content-Disposition')!.split('filename=')[1]!.split(';')[0];

            return res.blob();
        }
        return res.json();
    }).then((blob: any) => {
        if (blob.message || blob.statusCode || blob.errors) {
            throw new Error(blob.message ?? blob);
        }

        let a = $("<a style='display: none;'/>");
        let url = window.URL.createObjectURL(blob);

        dispatch(setLoading(false));
        a.attr("href", url);
        a.attr("download", fileName);
        $("body").append(a);
        a[0].click();
        window.URL.revokeObjectURL(url);
        a.remove();
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveActivities = (request: any, callback?: Function): AppThunk => async (dispatch) => {
    let url = 'api/plmt/Activities';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setLoading(false));
        dispatch(setActivities(data));
        if (callback) {
            callback();
        }
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
        if (callback) {
            callback();
        }
    });
}

export const retrieveCountries = (): AppThunk => async (dispatch) => {
    let url = 'api/plmt/countries';
    let request = {
    }

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setCountries(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const retrieveSigningEntities = (request: any): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/signingEntities';
    let state = getState();

    dispatch(setLoading(true));
    request.user = state.site.user;
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setSigningEntities(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveParties = (request: any): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/parties';
    let state = getState();

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setParties(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveNotification = (callback: Function): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/notification';
    let state = getState();
    let request = {
        audience: _.endsWith(state.site.user?.email.toLowerCase(), 'hpe.com') ? 'Internal' : 'External',
        language: state.site.currentLanguage.value
    };

    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        if (data) {
            callback(true);
        }
        dispatch(setNotification(data));
    }).catch(err => {
        dispatch(setError(`${err}`));
    });
}

export const exportActivities = (request: any, callback: Function): AppThunk => async (dispatch) => {
    let url = 'api/plmt/exportActivities';
    let currentTime = new Date();
    let fileName = `plmtActivities_${format(currentTime, 'yyyyMMddhhmmss')}.xlsx`;

    if (request.fileType === 'csv') {
        fileName = `plmtActivities_${format(currentTime, 'yyyyMMddhhmmss')}.zip`;
    }
    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            Accept: "application/json, application/zip, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "Content-Type": "application/json"
        }
    }).then(res => {
        if (res.ok) {
            fileName = res.headers.get('Content-Disposition')!.split('filename=')[1]!.split(';')[0];

            return res.blob();
        }
        return res.json();
    }).then(blob => {
        if (blob.message || blob.statusCode || blob.errors) {
            throw new Error(blob.message ?? blob);
        }

        let a = $("<a style='display: none;'/>");
        let url = window.URL.createObjectURL(blob);

        dispatch(setLoading(false));
        a.attr("href", url);
        a.attr("download", fileName);
        $("body").append(a);
        a[0].click();
        window.URL.revokeObjectURL(url);
        a.remove();
        callback();
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(err.message ?? err));
    });
}

export const exportSummaries = (request: any): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/exportComplianceSummary';
    let currentTime = new Date();
    let fileName = `plmtGapAnalysis_${format(currentTime, 'yyyyMMddhhmmss')}.xlsx`;

    if (request.fileType === 'csv') {
        fileName = `plmtGapAnalysis_${format(currentTime, 'yyyyMMddhhmmss')}.zip`;
    }
    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            Accept: "application/json, application/zip, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "Content-Type": "application/json"
        }
    }).then(res => {
        if (res.ok) {
            fileName = res.headers.get('Content-Disposition')!.split('filename=')[1]!.split(';')[0];

            return res.blob();
        }
        return res.json();
    }).then(blob => {
        if (blob.message || blob.statusCode || blob.errors) {
            throw new Error(blob.message ?? blob);
        }

        let a = $("<a style='display: none;'/>");
        let url = window.URL.createObjectURL(blob);

        dispatch(setLoading(false));
        a.attr("href", url);
        a.attr("download", fileName);
        $("body").append(a);
        a[0].click();
        window.URL.revokeObjectURL(url);
        a.remove();
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveComplianceSummary = (request: any): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/complianceSummary';
    let state = getState();

    dispatch(setLoading(true));
    request.user = state.site.user;
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setComplianceSummaries(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveComplianceAnalysis = (request: any): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/complianceAnalysis';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setGapAnalysis(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveDistributors = (request: any, callback?: Function): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/Distributors';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setLoading(false));
        dispatch(setDistributors(data));
        if (callback) {
            callback();
        }
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
        if (callback) {
            callback();
        }
    });
}

export const saveDistributors = (request: any, callback?: Function): AppThunk => async (dispatch) => {
    let url = 'api/plmt/saveDistributors';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setLoading(false));
        dispatch(setDistributors(data));
        if (callback) {
            callback();
        }
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
        if (callback) {
            callback();
        }
    });
}

export const retrievComplianceReports = (): AppThunk => async (dispatch, getState) => {
    let state = getState();
    let url = 'api/plmt/complianceReports';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(state.site.user),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setComplianceReports(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const retrievComplianceReport = (report: ComplianceReport): AppThunk => async (dispatch, getState) => {
    let state = getState();
    let url = 'api/plmt/retrievComplianceReport';

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(report),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setComplianceSummaries(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const downloadComplianceReport = (report: ComplianceReport): AppThunk => async (dispatch) => {
    let url = 'api/plmt/downloadComplianceReport';
    let currentTime = new Date();
    let fileName = `plmt_ComplianceReport_${report.name}_${format(currentTime, 'yyyyMMdd-hhmmss')}.xlsx`;
    let pattern = /^.*(plmt_.*\.xlsx)/i;

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(report),
        headers: {
            Accept: "application/json, application/zip, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "Content-Type": "application/json"
        }
    }).then(res => {
        if (res.ok) {
            let downloadName = res.headers.get('Content-Disposition');

            if (downloadName) {
                let result = downloadName.match(pattern);

                if (result) {
                    fileName = decodeURIComponent(result[1]);
                }
            }
            return res.blob();
        }
        return res.json();
    }).then(blob => {
        if (blob.message || blob.statusCode || blob.errors) {
            throw new Error(blob.message ?? blob);
        }

        let a = $("<a style='display: none;'/>");
        let url = window.URL.createObjectURL(blob);

        dispatch(setLoading(false));
        a.attr("href", url);
        a.attr("download", fileName);
        $("body").append(a);
        a[0].click();
        window.URL.revokeObjectURL(url);
        a.remove();
    }).catch(err => {
        dispatch(setLoading(false));
        dispatch(setError(`${err}`));
    });
}

export const retrieveComplianceOptions = (request: any, callback: Function): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/complianceOptions';
    let state = getState();
    let isLoading = state.site.loading;

    request.user = state.site.user;
    if (!isLoading) {
        dispatch(setLoading(true));
    }
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        if (!isLoading) {
            dispatch(setLoading(false));
        }
        callback(data);
    }).catch(err => {
        if (!isLoading) {
            dispatch(setLoading(false));
        }
        dispatch(setError(`${err}`));
    });
}

export const retrieveCompliancePartners = (): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/compliancePartners';
    let state = getState();

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(state.site.user),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setCompliancePartners(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const saveComplianceReport = (report: ComplianceReport, callback: Function): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/saveComplianceReport';
    let state = getState();
    let request = {
        user: state.site.user,
        report: report
    };

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setLoading(false));
        callback(data);
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const deleteComplianceReport = (report: ComplianceReport, callback: Function): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/deleteComplianceReport';
    let state = getState();
    let request = {
        user: state.site.user,
        report: report
    };

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setLoading(false));
        callback();
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const retrieveGeographies = (): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/getReportGeographies';
    let state = getState();

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(state.site.user),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setGeographies(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export const retrievePrpData = (id: string): AppThunk => async (dispatch, getState) => {
    let url = 'api/plmt/retrievePrpData';
    let state = getState();
    let request = {
        id: id
    };

    dispatch(setLoading(true));
    fetch(url, {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
            "Content-Type": "application/json"
        }
    }).then(res => {
        return (res.status == 204)
            ? null
            : res.json();
    }).then(data => {
        if (data?.statusCode) {
            throw Error(data.message);
        }
        dispatch(setPrpData(data));
        dispatch(setLoading(false));
    }).catch(err => {
        dispatch(setError(`${err}`));
        dispatch(setLoading(false));
    });
}

export default plmtSlice.reducer;
